import { useState, useEffect } from 'react';
import { LayoutGrid, AlertTriangle, RefreshCw, Inbox } from 'lucide-react';
import { plvApi, getTypeLabel } from '../api/plvApi';
import { ProductCard } from './ProductCard';
import { ProductModal } from './ProductModal';
import type { PLVProduct } from '../types/plv.types';
import { useAuthStore } from '../../../stores/auth-store';

export function ProductGrid() {
  const [products, setProducts] = useState<PLVProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<PLVProduct | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const { currentUser } = useAuthStore();
  const societyId = currentUser?.societyId;

  useEffect(() => {
    if (societyId) loadProducts();
  }, [societyId]);

  const loadProducts = async () => {
    if (!societyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await plvApi.getProducts(societyId);
      const activeProducts = data
        .filter(p => !p.isArchived)
        .sort((a, b) => a.name.localeCompare(b.name));
      setProducts(activeProducts);
    } catch {
      setError('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p => filterType === 'all' || p.type === filterType);
  const productTypes = [...new Set(products.map(p => p.type))];

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
        <div className="tw-w-8 tw-h-8 tw-border-4 tw-border-affinicar-secondary tw-border-t-transparent tw-rounded-full tw-animate-spin" />
        <p className="tw-mt-3 tw-text-gray-500">Chargement des produits...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-text-red-700">
        <AlertTriangle className="tw-w-5 tw-h-5 tw-flex-shrink-0" />
        <span>{error}</span>
        <button
          className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded tw-border tw-border-red-300 tw-text-red-700 hover:tw-bg-red-100 tw-transition-colors"
          onClick={loadProducts}
        >
          <RefreshCw className="tw-w-4 tw-h-4" />
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Header with filters */}
      <div className="tw-mb-4">
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-gap-3">
            <div>
              <h4 className="tw-text-lg tw-font-semibold tw-flex tw-items-center tw-gap-2">
                <LayoutGrid className="tw-w-5 tw-h-5 tw-text-affinicar-secondary" />
                Catalogue PLV
              </h4>
              <p className="tw-text-sm tw-text-gray-500 tw-mt-0.5">
                {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} disponible
                {filteredProducts.length > 1 ? 's' : ''}
              </p>
            </div>
            {productTypes.length > 1 && (
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                <button
                  className={`tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-transition-colors ${
                    filterType === 'all'
                      ? 'tw-bg-affinicar-secondary tw-text-white tw-border-affinicar-secondary'
                      : 'tw-bg-white tw-text-gray-600 tw-border-gray-300 hover:tw-bg-gray-50'
                  }`}
                  onClick={() => setFilterType('all')}
                >
                  Tous
                </button>
                {productTypes.map(type => (
                  <button
                    key={type}
                    className={`tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-transition-colors ${
                      filterType === type
                        ? 'tw-bg-affinicar-secondary tw-text-white tw-border-affinicar-secondary'
                        : 'tw-bg-white tw-text-gray-600 tw-border-gray-300 hover:tw-bg-gray-50'
                    }`}
                    onClick={() => setFilterType(type)}
                  >
                    {getTypeLabel(type)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Products grid */}
      {filteredProducts.length === 0 ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-gray-400">
          <Inbox className="tw-w-16 tw-h-16 tw-mb-3" />
          <h4 className="tw-text-lg tw-font-medium tw-text-gray-500">Aucun produit disponible</h4>
          <p className="tw-text-sm tw-mt-1">
            {filterType === 'all'
              ? "Aucun produit n'est actuellement disponible."
              : `Aucun produit de type "${getTypeLabel(filterType)}" n'est disponible.`}
          </p>
          {filterType !== 'all' && (
            <button
              className="tw-mt-3 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-affinicar-secondary tw-text-affinicar-secondary hover:tw-bg-affinicar-lighter tw-transition-colors"
              onClick={() => setFilterType('all')}
            >
              Voir tous les produits
            </button>
          )}
        </div>
      ) : (
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
          {filteredProducts.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onImageClick={() => setSelectedProduct(product)}
            />
          ))}
        </div>
      )}

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </>
  );
}
